import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, NavLink, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCouncilApi, ACTING_AS_KEY } from './councilApi';
import { OFFICE_LABELS } from './offices';
import { Spinner, selectClass } from '../admin/AdminUI';
import CouncilDashboardTab from './CouncilDashboardTab';
import DecisionsTab from './DecisionsTab';
import CouncilDocumentsTab from './CouncilDocumentsTab';
import InvoicesTab from './InvoicesTab';
import CouncilVendorsTab from './CouncilVendorsTab';

const TABS = [
  { key: 'dashboard', label: 'Dashboard', component: CouncilDashboardTab },
  { key: 'decisions', label: 'Decisions', component: DecisionsTab },
  { key: 'invoices', label: 'Invoices', component: InvoicesTab },
  { key: 'vendors', label: 'Vendors', component: CouncilVendorsTab },
  { key: 'documents', label: 'Documents', component: CouncilDocumentsTab }
];

function NotAvailable() {
  return <p className="px-4 md:px-8 py-10 text-muted-foreground">The Council portal isn't available here.</p>;
}

function CouncilPicker() {
  const api = useCouncilApi(null);
  const [condos, setCondos] = useState(null);
  useEffect(() => {
    api.get('').then((r) => setCondos(r.data.condos || [])).catch(() => setCondos([]));
  }, [api]);
  if (!condos) return <Spinner />;
  if (condos.length === 0) return <NotAvailable />;
  return <Navigate to={`/council/${condos[0].slug}`} replace />;
}

// Admin preview only: whom the admin is testing as. Saved per condo for this browser tab.
function ActingAsPicker({ info, actingAs, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm rounded-lg border border-warning px-3 py-2">
      <span className="text-warning font-medium">Admin preview</span>
      <span className="text-muted-foreground">Acting as</span>
      <select aria-label="Acting as" value={actingAs || ''} onChange={(e) => onChange(e.target.value || null)} className={selectClass}>
        <option value="">Nobody (read only)</option>
        {info.members.map((m) => (
          <option key={m._id} value={m._id}>{m.user.name}, {OFFICE_LABELS[m.office]}</option>
        ))}
      </select>
      <span className="text-xs text-muted-foreground">Everything you do here is marked test.</span>
    </div>
  );
}

function CouncilPortal({ slug, tab }) {
  const [actingAs, setActingAs] = useState(() => sessionStorage.getItem(ACTING_AS_KEY(slug)) || null);
  const api = useCouncilApi(actingAs);
  const [info, setInfo] = useState(null);

  const load = useCallback(() => {
    api.get(`/${slug}`).then((r) => setInfo(r.data)).catch(() => setInfo({ unavailable: true }));
  }, [api, slug]);

  useEffect(() => { load(); }, [load]);

  const chooseActingAs = (id) => {
    if (id) sessionStorage.setItem(ACTING_AS_KEY(slug), id);
    else sessionStorage.removeItem(ACTING_AS_KEY(slug));
    setActingAs(id);
  };

  if (!info) return <Spinner />;
  if (info.unavailable) return <NotAvailable />;
  const current = TABS.find((t) => t.key === tab);
  if (!current) return <Navigate to={`/council/${slug}`} replace />;
  const TabComponent = current.component;
  const me = info.membership;

  return (
    <div className="min-h-screen bg-background">
      <div className="px-4 md:px-8 py-6 pb-20 md:pb-8">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-4">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">
              <span className="px-2 py-0.5 rounded bg-audienceOwners/20 text-audienceOwners font-medium">Council</span>
              {me && <> · {me.user.name}, {OFFICE_LABELS[me.office]}</>}
            </p>
            <h1 className="text-xl font-bold text-foreground truncate mt-1">{info.condo.name}</h1>
          </div>
          {info.mode === 'preview' && (
            <div className="lg:ml-auto">
              <ActingAsPicker info={info} actingAs={actingAs} onChange={chooseActingAs} />
            </div>
          )}
        </div>
        <nav aria-label="Council sections" className="flex gap-1 overflow-x-auto border-b border-border mb-6">
          {TABS.map(({ key, label }) => (
            <NavLink
              key={key}
              to={key === 'dashboard' ? `/council/${slug}` : `/council/${slug}/${key}`}
              end
              className={() => `whitespace-nowrap px-4 py-2 border-b-2 text-sm transition-colors ${
                tab === key ? 'border-primary text-foreground font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <TabComponent key={actingAs || 'none'} slug={slug} info={info} api={api} />
      </div>
    </div>
  );
}

function CouncilPage() {
  const { condoSlug, tab } = useParams();
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!condoSlug) return <CouncilPicker />;
  return <CouncilPortal key={condoSlug} slug={condoSlug} tab={tab || 'dashboard'} />;
}

export default CouncilPage;
